import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import { HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { HdPhsInfoService } from '../../hd-phs-info.service';
import { PhsCategory } from '../../hd-phs-metadata';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';

/**
 * Renders one of the four "Transformation" arrows (Determination, Environment,
 * Motivation, Perspective) used in the Human Design variables.
 *
 * Direction is derived from the supplied tone:
 *   - tone 1-3 → arrow points LEFT
 *   - tone 4-6 → arrow points RIGHT
 *
 * Color follows the side: design = red, personality = black, taken from the
 * planet-grid color config so themes stay consistent with the Color/Tone block.
 */
@Component({
  selector: 'hd-transformation-arrow',
  templateUrl: './hd-transformation-arrow.component.html',
  styleUrl: './hd-transformation-arrow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.role]': 'interactive() ? "button" : null',
    '[attr.tabindex]': 'interactive() ? 0 : null',
    '[attr.aria-label]': 'accessibleLabel()',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()',
    '(click)': 'emitBlockClicked($event)',
    '(keydown.enter)': 'emitBlockClicked($event)',
    '(keydown.space)': 'emitBlockClicked($event)',
    '[class.is-hover-match]': 'isHoverMatch()',
    '[class.is-hover-source]': 'isHoverSource()',
    '[class.is-hover-dimmed]': 'isHoverDimmed()',
    '[class.is-dim-disabled]': 'isDimDisabled()',
  },
})
export class HdTransformationArrowComponent {
  private readonly hostElementRef = inject(ElementRef<HTMLElement>);
  private readonly phsInfo = inject(HdPhsInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /**
   * Klik-event op de transformatie-pijl. Spiegelt de
   * {@link HdColorToneBlockComponent#phsBlockClicked} signatuur zodat de
   * cross-parent dezelfde verrijking + bubble-route kan gebruiken voor het
   * openen van het PHS-info-panel — de pijl en het color/tone-blok horen
   * inhoudelijk bij dezelfde variabele.
   */
  readonly phsBlockClicked = output<{ origin: { x: number; y: number } }>();

  readonly tone = input.required<number>();
  readonly side = input.required<HdBodygraphSide>();
  /** PHS/Rave-Psychologie-context die bepaalt welk pijl-keynotepaar geldt. */
  readonly category = input<PhsCategory | null>(null);
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();
  /** `false` voor decoratieve herhalingen van de pijl, zoals in het info-panel. */
  readonly interactive = input(true);
  /** Toon de categoriegebonden pijl-keynote als hover-/focuskaart boven het blok. */
  readonly showHdKeynotes = input(false);
  /**
   * Poorten waar deze transformatiepijl inhoudelijk bij hoort (motivatie =
   * Sun + Earth, view = NN + SN). Triggert dezelfde chart-highlight als
   * een directe gate-hover.
   */
  readonly relatedGates = input<readonly number[]>([]);

  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });
  private readonly hostHovered = signal(false);
  private readonly hostFocused = signal(false);

  protected readonly pointsRight = computed(() => this.tone() >= 4);

  protected readonly fillColor = computed(() => {
    const shape = this.colorConfig().colorToneShape;
    return this.side() === 'design' ? shape.design : shape.personality;
  });

  private readonly keynote = computed(() => {
    const category = this.category();
    if (category === null) return null;
    const brain = this.phsInfo.categoryInfoIn(category, this.contentLang.effectiveLang()).brain;
    return this.pointsRight() ? brain.right : brain.left;
  });

  protected readonly accessibleLabel = computed(() => {
    if (!this.interactive()) return null;
    return this.keynote()?.name.trim() || null;
  });

  protected readonly visibleKeynote = computed(() => {
    if (!this.showHdKeynotes() || (!this.hostHovered() && !this.hostFocused())) return null;
    const keynote = this.keynote();
    return keynote?.name.trim() ? keynote : null;
  });

  protected readonly isHoverMatch = computed(() => {
    const service = this.hoverService;
    if (!service?.enabled() || !service?.isActive()) return false;
    const gates = this.relatedGates();
    return gates.some(gate => service.isGateMatchingHover(gate));
  });

  protected readonly isHoverSource = computed(() => {
    const service = this.hoverService;
    if (!service?.enabled() || !service?.isActive()) return false;
    return this.hostHovered() && service.state()?.source === 'planet-grid-furnishing';
  });

  protected readonly isHoverDimmed = computed(() => {
    const service = this.hoverService;
    if (!service?.enabled() || !service?.isActive()) return false;
    if (!service.dimsGrid()) return false;
    return !this.isHoverMatch();
  });

  /** "Dimmen bij hover" uit: matches tonen dan de blauwe rand (zie SCSS). */
  protected readonly isDimDisabled = computed(() =>
    this.hoverService != null && !this.hoverService.dimOnHover(),
  );

  protected onMouseEnter(): void {
    this.hostHovered.set(true);
    this.hoverService?.setGatesHover(this.relatedGates(), 'planet-grid-furnishing');
  }

  protected onMouseLeave(): void {
    this.hostHovered.set(false);
    this.hoverService?.setGatesHover(null, 'planet-grid-furnishing');
  }

  protected onFocus(): void {
    this.hostFocused.set(true);
  }

  protected onBlur(): void {
    this.hostFocused.set(false);
  }

  /** Host-listener voor LMB en Enter/Space — emit het cursor- of midden-blokpunt. */
  protected emitBlockClicked(event: Event): void {
    if (!this.interactive()) return;
    event.preventDefault();
    event.stopPropagation();
    this.phsBlockClicked.emit({ origin: this.resolveClickOrigin(event) });
  }

  private resolveClickOrigin(event: Event): { x: number; y: number } {
    if (event instanceof MouseEvent && (event.clientX !== 0 || event.clientY !== 0)) {
      return { x: event.clientX, y: event.clientY };
    }
    const rect = this.hostElementRef.nativeElement.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }
}
